import React from 'react';
import { RadioGroup, FormControlLabel, Radio, TextField, Typography } from '@mui/material';
import moment from 'moment';

export interface SessionSchedulePickerProps {
  modeLabelNow?: string;
  modeLabelSchedule?: string;
  modeLabelDoNotStart?: string;
  showDoNotStart?: boolean;
  scheduledDate: Date | null;
  onChange: (startNow: boolean, scheduledDate: Date | null) => void;
  error?: string | null;
  setError?: (err: string | null) => void;
}

const IMMEDIATE = "startNow";
const DO_NOT_START = "doNotStart";
const SCHEDULED = "schedule";
const DATE_TIME_LOCAL_FORMAT = "YYYY-MM-DDTHH:mm";

export const SessionSchedulePicker: React.FC<SessionSchedulePickerProps> = ({
  modeLabelNow = "Start Now",
  modeLabelSchedule = "Schedule Start",
  modeLabelDoNotStart = "Do Not Start",
  showDoNotStart = false,
  scheduledDate,
  onChange,
  error,
  setError,
}) => {
  const initialMode = scheduledDate
    ? SCHEDULED
    : IMMEDIATE;

  const [immediateOrScheduled, setImmediateOrScheduled] = React.useState(initialMode);
  const [userScheduledDate, setUserScheduledDate] = React.useState<Date | null>(scheduledDate);

  React.useEffect(() => {
    setImmediateOrScheduled(scheduledDate ? SCHEDULED : IMMEDIATE);
    setUserScheduledDate(scheduledDate);
  }, [scheduledDate]);

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = (event.target as HTMLInputElement).value;
    setImmediateOrScheduled(value);
    setError?.(null);

    if (value === IMMEDIATE) {
      onChange(true, userScheduledDate);
    } else if (value === SCHEDULED) {
      onChange(false, userScheduledDate);
    } else if (value === DO_NOT_START) {
      onChange(false, null);
    }
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = moment(event.target.value, DATE_TIME_LOCAL_FORMAT).toDate();
    setUserScheduledDate(newDate);
    setError?.(null);
    onChange(immediateOrScheduled === IMMEDIATE, newDate);
  };

  const disableDateField =
    immediateOrScheduled === IMMEDIATE || immediateOrScheduled === DO_NOT_START;

  return (
    <>
      <RadioGroup value={immediateOrScheduled} onChange={handleOptionChange}>
        <FormControlLabel
          value={IMMEDIATE}
          control={<Radio />}
          label={modeLabelNow}
        />

        {showDoNotStart && (
          <FormControlLabel
            value={DO_NOT_START}
            control={<Radio />}
            label={modeLabelDoNotStart}
          />
        )}

        <FormControlLabel
          value={SCHEDULED}
          control={<Radio />}
          label={modeLabelSchedule}
        />
      </RadioGroup>

      <TextField
        label={modeLabelSchedule}
        type="datetime-local"
        variant="standard"
        value={
          userScheduledDate
            ? moment(userScheduledDate).format(DATE_TIME_LOCAL_FORMAT)
            : moment(new Date()).format(DATE_TIME_LOCAL_FORMAT)
        }
        disabled={disableDateField}
        onChange={handleDateChange}
        fullWidth
        margin="dense"
      />

      {error && (
        <Typography color="error" variant="body2" sx={{ marginTop: 1 }}>
          {error}
        </Typography>
      )}
    </>
  );
};
